import { Component, type ReactNode } from 'react';
import './ChatErrorBoundary.css';

interface ChatErrorBoundaryProps {
  children: ReactNode;
}

interface ChatErrorBoundaryState {
  hasError: boolean;
}

/**
 * Error boundary that catches initialization failures in ChatInterface.
 * Displays a service-unavailable message with a retry option that remounts
 * the child component tree.
 *
 * Requirements: 1.4
 */
export class ChatErrorBoundary extends Component<
  ChatErrorBoundaryProps,
  ChatErrorBoundaryState
> {
  constructor(props: ChatErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ChatErrorBoundaryState {
    return { hasError: true };
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="chat-error-boundary" role="alert">
          <div className="chat-error-boundary__content">
            <p className="chat-error-boundary__message">
              The chat service is temporarily unavailable. Please try again.
            </p>
            <button
              type="button"
              className="chat-error-boundary__retry-button"
              onClick={this.handleRetry}
            >
              ↻ Retry
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
